import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import api from '../../utils/functions/api';
import AiRunStatus from './AiRunStatus';
import DailyWorkspace from './DailyWorkspace';
import ExecutiveProjectReport, { openProjectInErp } from './ExecutiveProjectReport';

const API = `${process.env.REACT_APP_API_URL}/ai-followup`;

// สี: ลด/เสียหาย = แดง, เพิ่ม = เขียว, ประเด็นจาก AI = ส้ม
const TYPE_STYLE = {
  profit_down: { bg: '#fef2f2', border: '#dc2626', label: 'กำไรลดลง' },
  profit_up: { bg: '#f0fdf4', border: '#16a34a', label: 'กำไรเพิ่มขึ้น' },
  big_expense: { bg: '#fff7ed', border: '#ea580c', label: 'ค่าใช้จ่ายก้อนใหญ่' },
  ai_finding: { bg: '#fefce8', border: '#ca8a04', label: 'ประเด็นจาก AI' },
  escalated_question: { bg: '#fef2f2', border: '#7f1d1d', label: 'ส่งต่อจากเจ้าหน้าที่' },
  budget_over: { bg: '#fef2f2', border: '#b91c1c', label: 'งบ BG เกิน' },
  budget_risk: { bg: '#fef2f2', border: '#dc2626', label: 'เสี่ยงขาดทุน' },
  delivery_risk: { bg: '#fff7ed', border: '#c2410c', label: 'เสี่ยงส่งมอบช้า' },
  billing_receipt: { bg: '#fefce8', border: '#a16207', label: 'บิล/เงินรับ' },
  decision_needed: { bg: '#eff6ff', border: '#1d4ed8', label: 'รอตัดสินใจ' },
};

const styleOf = (a) => TYPE_STYLE[a.type === 'profit_step' ? `profit_${a.direction}` : a.type] || TYPE_STYLE.ai_finding;

// หน้า Executive Alerts — แสดงรายงานรายวันของระบบ AI ติดตามโครงการ (ช่องทางเดียว ไม่ส่ง LINE/Discord/Push)
function ExecutiveAlerts({ title = 'Executive Alerts', subtitle = '' }) {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState('');
  const [report, setReport] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  // คำถามจาก AI ที่รอหัวหน้าตรวจก่อนแสดง — เห็นเฉพาะผู้มีอำนาจ (เช็คสิทธิ์ที่เซิร์ฟเวอร์)
  const [pending, setPending] = useState([]);
  const [isAuthority, setIsAuthority] = useState(false);
  const [tab, setTab] = useState('alerts');
  const [reportId, setReportId] = useState(null);

  const loadPending = useCallback(async () => {
    try {
      const me = await api.get(`${API}/me`);
      setIsAuthority(!!me.data?.is_authority);
      if (me.data?.is_authority) {
        const { data } = await api.get(`${API}/questions-pending-review`);
        setPending(data || []);
      }
    } catch (e) {
      setPending([]);
    }
  }, []);

  const review = async (q, action) => {
    await api.post(`${API}/questions/${q._id}/review`, { action });
    await loadPending();
  };

  // อนุมัติทุกข้อที่รอตรวจในครั้งเดียว (ทีละข้อต่อกัน กันชนกันที่เซิร์ฟเวอร์) — ข้อที่ล้มเหลวจะยังค้างในรายการ
  const [approveAllOpen, setApproveAllOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const approveAll = async () => {
    setApproving(true);
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const q of pending) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`${API}/questions/${q._id}/review`, { action: 'approve' });
      }
    } finally {
      setApproving(false);
      setApproveAllOpen(false);
      await loadPending();
    }
  };

  // v ไม่ส่ง = ฉบับล่าสุดของวัน; ส่ง = เปิดดูฉบับเก่าที่ถูกแทนที่แล้ว (ระบบบันทึกว่าคุณเปิดดูฉบับไหน)
  const load = useCallback(async (d, v) => {
    setLoading(true);
    try {
      const params = {};
      if (d) params.date = d;
      if (v) params.version = v;
      const { data } = await api.get(`${API}/reports`, { params });
      setReport(data);
      setDate(data?.date || d || '');
      if (data?.date) {
        const res = await api.get(`${API}/reports/versions`, { params: { date: data.date } });
        setVersions(res.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${API}/reports/dates`);
        setDates(data || []);
      } catch (e) {
        setDates([]);
      }
      load('');
      loadPending();
    })();
  }, [load, loadPending]);

  const alerts = report?.alerts || [];
  const t = report?.totals || {};

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="alerts" label="แจ้งเตือนผู้บริหาร" />
        <Tab value="workspace" label="งานติดตามรายวัน" />
        <Tab value="status" label="สถานะการรัน" />
      </Tabs>
      {tab === 'workspace' && <Box sx={{ mt: 2 }}><DailyWorkspace /></Box>}
      {tab === 'status' && <Box sx={{ mt: 2 }}><AiRunStatus /></Box>}
      {tab === 'alerts' && (
        <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 2 }} flexWrap="wrap">
        <Select size="small" value={date} displayEmpty onChange={(e) => load(e.target.value)} sx={{ minWidth: 160 }}>
          {date && !dates.some((d) => d.date === date) && <MenuItem value={date}>{date}</MenuItem>}
          {dates.map((d) => (
            <MenuItem key={d.date} value={d.date}>{d.date}</MenuItem>
          ))}
        </Select>
        <Chip size="small" label={`กำไรลดลง ${t.profit_down || 0}`} sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
        <Chip size="small" label={`กำไรเพิ่มขึ้น ${t.profit_up || 0}`} sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
        <Chip size="small" label={`ค่าใช้จ่ายก้อนใหญ่ ${t.big_expense || 0}`} sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 600 }} />
        <Chip size="small" label={`ประเด็นจาก AI ${t.ai_finding || 0}`} sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 600 }} />
        <Chip size="small" label={`ส่งต่อจากเจ้าหน้าที่ ${t.escalated_question || 0}`} sx={{ bgcolor: '#fee2e2', color: '#7f1d1d', fontWeight: 600 }} />
        <Chip size="small" label={`งบเกิน ${t.budget_over || 0}`} sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
        <Chip size="small" label={`เสี่ยงขาดทุน ${t.budget_risk || 0}`} sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
        <Chip size="small" label={`เสี่ยงส่งมอบช้า ${t.delivery_risk || 0}`} sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 600 }} />
        <Chip size="small" label={`บิล/เงินรับ ${t.billing_receipt || 0}`} sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 600 }} />
        <Chip size="small" label={`รอตัดสินใจ ${t.decision_needed || 0}`} sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }} />
        {report && (
          <Chip
            size="small"
            color={report.phase === 'updated' ? 'info' : 'default'}
            label={report.phase === 'updated' ? 'ฉบับปรับปรุง' : 'ฉบับเบื้องต้น (08:30)'}
          />
        )}
        {report?.stale && <Chip size="small" color="warning" label="ข้อมูลถูกแก้ย้อนหลัง รายงานจะสร้างใหม่" />}
        {report && (
          <Select
            size="small"
            value={report.version}
            onChange={(e) => load(date, e.target.value)}
            sx={{ minWidth: 150 }}
          >
            {(versions.length ? versions : [report]).map((v) => (
              <MenuItem key={v.version} value={v.version}>
                {`ฉบับที่ ${v.version}${v.is_latest ? ' (ล่าสุด)' : ' (ถูกแทนที่)'}`}
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
      {report && !report.is_latest && (
        <Typography variant="body2" sx={{ mb: 1, color: '#9a3412', fontWeight: 600 }}>
          คุณกำลังดูฉบับเก่าที่ถูกแทนที่แล้ว — ฉบับล่าสุดของวันนี้คือฉบับที่ {versions.find((v) => v.is_latest)?.version}
        </Typography>
      )}
      {report?.replaces && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
          ฉบับนี้มาแทนฉบับที่ {report.replaces.version}
          {(() => {
            const rs = report.replaces.reasons || [];
            const fixes = rs.filter((r) => !r.type).length;
            const news = rs.length - fixes;
            const parts = [];
            if (fixes) parts.push(`มีการแก้ไขข้อมูลย้อนหลัง ${fixes} รายการ`);
            if (news) parts.push(`มีคำตอบ/การส่งต่อใหม่ ${news} รายการ`);
            return parts.length ? ` เพราะ${parts.join(' และ ')}` : '';
          })()}
        </Typography>
      )}
      {isAuthority && pending.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fffbeb', borderColor: '#f59e0b' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {`คำถามจาก AI ที่รอคุณตรวจก่อนแสดงให้เจ้าหน้าที่ (${pending.length})`}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setApproveAllOpen(true)}>
              อนุมัติทั้งหมด
            </Button>
          </Stack>
          <Stack spacing={1}>
            {pending.map((q) => (
              <Box key={q._id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{q.project_label}</Typography>
                  <Typography variant="body2">{q.text}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`ที่มา: ${q.source?.label || '-'} · ความสำคัญ ${{ high: 'สูง', medium: 'กลาง', low: 'ต่ำ' }[q.priority] || '-'}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Button size="small" variant="contained" onClick={() => review(q, 'approve')}>อนุมัติ</Button>
                  <Button size="small" color="inherit" onClick={() => review(q, 'dismiss')}>ไม่อนุมัติ</Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
      <Dialog open={approveAllOpen} onClose={() => !approving && setApproveAllOpen(false)}>
        <DialogTitle>อนุมัติคำถามทั้งหมด</DialogTitle>
        <DialogContent>
          <Typography>
            {`อนุมัติคำถามจาก AI ทั้ง ${pending.length} ข้อ และแสดงให้เจ้าหน้าที่ของแต่ละโครงการเห็นทันทีใช่ไหม?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus color="inherit" disabled={approving} onClick={() => setApproveAllOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" disabled={approving} onClick={approveAll}>
            {approving ? 'กำลังอนุมัติ...' : 'ยืนยันอนุมัติทั้งหมด'}
          </Button>
        </DialogActions>
      </Dialog>
      {!loading && alerts.length === 0 && (
        <Typography color="text.secondary">{report?.summary_line || 'ไม่มีเรื่องที่ต้องแจ้งในวันนี้'}</Typography>
      )}
      {!loading && alerts.length > 0 && report?.summary_line && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{report.summary_line}</Typography>
      )}
      <Stack spacing={1}>
        {alerts.map((a, i) => {
          const s = styleOf(a);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Paper key={i} variant="outlined" sx={{ p: 1.5, bgcolor: s.bg, borderLeft: `5px solid ${s.border}` }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={s.label} sx={{ fontWeight: 600 }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{a.label}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{a.title}</Typography>
              <Typography variant="body2" color="text.secondary">{a.detail}</Typography>
              {a.project_id && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                  <Button size="small" onClick={() => setReportId(a.project_id)}>รายงานโครงการ</Button>
                  <Button size="small" color="inherit" onClick={() => openProjectInErp(a.project_id)}>เปิดโครงการ</Button>
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>
        </>
      )}
      <ExecutiveProjectReport projectId={reportId} open={!!reportId} onClose={() => setReportId(null)} />
    </Box>
  );
}

export default ExecutiveAlerts;
